import { useState, useEffect } from "react";
import axios from "axios";
import MUIDataTable from "mui-datatables";
import { styled } from "@mui/material/styles";
import {
  Chip,
  Button,
  Box,
  Tooltip,
  Typography,
  TextField,
  Alert,
  Snackbar,
  Modal,
} from "@mui/material";
import { useSelector } from "react-redux";
import ViewTicketModal from "../Admin/Pop-up/ClientDashboardModal/ViewTicketModal";
import RemarkModal from "../Admin/Pop-up/ClientDashboardModal/RemarkModal";
import { BASE_URL } from "../../components/config/config";

const options = {
  filterType: "dropdown",
  download: false,
  print: false,
  selectableRows: "none",
};

const NoMaxWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .MuiTooltip-tooltip`]: {
    maxWidth: "none",
  },
});

const PREFIX = "ClientRequests";

const classes = {
  root: `${PREFIX}-root`,
  detailSection: `${PREFIX}-detailSection`,
  detailTitle: `${PREFIX}-detailTitle`,
  dialogTitle: `${PREFIX}-dialogTitle`,
  remarkPaper: `${PREFIX}-remarkPaper`,
  remarkList: `${PREFIX}-remarkList`,
  timelineWrapper: `${PREFIX}-timelineWrapper`,
  timelineHeader: `${PREFIX}-timelineHeader`,
  timeline: `${PREFIX}-timeline`,
  track: `${PREFIX}-track`,
  step: `${PREFIX}-step`,
  dot: `${PREFIX}-dot`,
  dotActive: `${PREFIX}-dotActive`,
  dotCompleted: `${PREFIX}-dotCompleted`,
  dotCanceled: `${PREFIX}-dotCanceled`,
  label: `${PREFIX}-label`,
  subLabel: `${PREFIX}-subLabel`,
  time: `${PREFIX}-time`,
  section: `${PREFIX}-section`,
  sectionTitle: `${PREFIX}-sectionTitle`,
};

const Root = styled("div")(({ theme }) => ({
  [`&.${classes.root}`]: {
    padding: theme.spacing(2),
  },
  [`& .${classes.detailSection}`]: {
    marginBottom: theme.spacing(3),
  },
  [`& .${classes.detailTitle}`]: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  [`& .${classes.dialogTitle}`]: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  [`& .${classes.remarkPaper}`]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  [`& .${classes.remarkList}`]: {
    maxHeight: 280,
    overflowY: "auto",
    marginTop: theme.spacing(2),
  },
  [`& .${classes.timelineWrapper}`]: {
    margin: theme.spacing(4, 0),
  },
  [`& .${classes.timelineHeader}`]: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  [`& .${classes.timeline}`]: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(4, 2, 2),
  },
  [`& .${classes.track}`]: {
    position: "absolute",
    top: "50%",
    left: theme.spacing(4),
    right: theme.spacing(4),
    height: 6,
    borderRadius: 3,
    background: theme.palette.grey[300],
    transform: "translateY(-50%)",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      height: "100%",
      background: theme.palette.primary.main,
      width: "var(--progress, 0%)",
      transition: "width .4s ease",
    },
  },
  [`& .${classes.step}`]: {
    flex: 1,
    textAlign: "center",
    position: "relative",
  },
  [`& .${classes.dot}`]: {
    margin: "0 auto",
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: theme.palette.background.paper,
    border: `3px solid ${theme.palette.grey[400]}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: theme.shadows[2],
    transition: "all .3s ease",
  },
  [`& .${classes.dotActive}`]: {
    borderColor: theme.palette.primary.main,
    transform: "scale(1.1)",
  },
  [`& .${classes.dotCompleted}`]: {
    borderColor: theme.palette.success.main,
    background: theme.palette.success.light,
    "& svg": { color: theme.palette.success.main },
  },
  [`& .${classes.dotCanceled}`]: {
    borderColor: theme.palette.error.main,
    background: theme.palette.error.light,
    "& svg": { color: theme.palette.error.main },
  },
  [`& .${classes.label}`]: {
    marginTop: theme.spacing(3),
    fontSize: 16,
    fontWeight: 600,
  },
  [`& .${classes.subLabel}`]: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  [`& .${classes.time}`]: {
    fontSize: 13,
    color: theme.palette.text.disabled,
    fontStyle: "italic",
  },
  [`& .${classes.section}`]: {
    marginBottom: theme.spacing(4),
  },
  [`& .${classes.sectionTitle}`]: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
}));

const ClientRequests = () => {
  const globalState = useSelector((state) => state.clientReducer);
  const clientId = globalState?.clientDetails?._id;

  // State management
  const [ticketsList, setTicketsList] = useState([]);
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    duration: 4000,
    type: "info",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [ticketForRemark, setTicketForRemark] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [ticketToCancel, setTicketToCancel] = useState(null);

  // Helper functions
  const handleOpenCancelModal = (ticket) => {
    setTicketToCancel(ticket);
    setCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setCancelModalOpen(false);
    setTicketToCancel(null);
    setRemarks("");
  };

  // Columns configuration
  const columns = [
    {
      name: "createdAt",
      label: "Created On",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <span>{new Date(value).toLocaleDateString()}</span>
        ),
      },
    },
    {
      name: "ticket_id",
      label: "Request ID",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "service_required",
      label: "Service Required",
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value) => (
          <Box sx={{ width: 200 }}>
            <NoMaxWidthTooltip title={value} arrow>
              <span>
                {value?.length > 40 ? `${value.slice(0, 40)}...` : value}
              </span>
            </NoMaxWidthTooltip>
          </Box>
        ),
      },
    },
    {
      name: "tstatus",
      label: "Request Status",
      options: {
        filter: true,
        filterType: "multiselect",
        sort: false,
        customBodyRender: (value) => (
          <Chip
            label={value}
            sx={{
              backgroundColor:
                value === "CLOSED"
                  ? "#FF9999"
                  : value === "CANCELED"
                  ? "#f44336"
                  : value === "Technician Assigned"
                  ? "#8FD1AD"
                  : value === "Technician Not Assigned"
                  ? "#f44336"
                  : value === "PENDING"
                  ? "#ff9800"
                  : value === "ACCEPTED"
                  ? "#2196f3"
                  : "red",
              color: "black",
              fontWeight: 500,
              textTransform: "uppercase",
            }}
          />
        ),
      },
    },
    {
      name: "customer_name",
      label: "Customer Name",
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "phone",
      label: "Customer Number",
      options: {
        filter: false,
        sort: false,
        // Display only last 3 digits
        customBodyRender: (value) =>
          value ? `*******${value.slice(-4)}` : "-",
        customSearch: (searchValue, rowData, columnMeta) => {
          const fullPhoneNumber = rowData[columnMeta.columnIndex];
          return fullPhoneNumber?.toString().includes(searchValue);
        },
        filterOptions: {
          names: [],
        },
      },
    },
    {
      name: "time_preference",
      label: "Time Preference",
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "ticket_created_by",
      label: "Request Created By",
      options: {
        filter: true,
        filterType: "multiselect",
        sort: false,
      },
    },
    {
      name: "action",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          const hasRemarks =
            value.remarks?.onboarding_team_remarks &&
            value.remarks.onboarding_team_remarks.length > 0;
          const isPending = value.ticket_status === "PENDING";

          return (
            <Box display="flex" gap={2} alignItems="center">
              <Tooltip title="View Request">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setSelectedTicket(value);
                    setViewModalOpen(true);
                  }}
                >
                  View
                </Button>
              </Tooltip>

              <Tooltip title="Remark">
                <Button
                  variant={hasRemarks ? "contained" : "outlined"}
                  style={{
                    backgroundColor: hasRemarks ? "#4caf50" : undefined,
                    color: hasRemarks ? "white" : undefined,
                    marginLeft: "10px",
                  }}
                  onClick={() => {
                    setTicketForRemark(value);
                    setRemarkModalOpen(true);
                  }}
                >
                  Remark
                </Button>
              </Tooltip>

              {isPending && (
                <Tooltip title="Cancel Request">
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "red", marginLeft: "10px" }}
                    onClick={() => handleOpenCancelModal(value)}
                  >
                    Cancel
                  </Button>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    },
  ];

  // Helper functions
  const showDialog = (open, type, msg) => {
    setToast({
      open,
      msg,
      duration: 5000,
      type,
    });
  };
  const closeDialog = () => {
    setToast({
      open: false,
      msg: "",
      duration: 5000,
      type: "",
    });
  };

  const handleRemarkAdded = (id, text, name) => {
    setTicketsList((prev) =>
      prev.map((t) =>
        t._id === id
          ? {
              ...t,
              remarks: {
                ...t.remarks,
                onboarding_team_remarks: text,
                onboarding_team_name: name,
              },
            }
          : t
      )
    );
  };

  const cancelTicket = async () => {
    if (!ticketToCancel || !remarks.trim()) return;

    try {
      setIsLoading(true);

      // First add the remark
      await axios.post(`${BASE_URL}admin/onboarding-team-remarks`, {
        onboarding_team_remarks: remarks,
        onboarding_team_name: globalState?.clientDetails?.name || "Client",
        ticket_obj_id: ticketToCancel._id,
      });

      // Then cancel the ticket
      await axios.post(`${BASE_URL}client/cancel-client-ticket`, {
        ticket_obj_id: ticketToCancel._id,
        client_id_obj: clientId,
      });
      // Refresh the tickets list
      await getAllTickets();

      showDialog(true, "success", "Request cancelled successfully");
      handleCloseCancelModal();
    } catch (error) {
      showDialog(true, "error", "Failed to cancel request");
    } finally {
      setIsLoading(false);
    }
  };

  const getTicketStatus = (ticket) => {
    if (ticket.ticket_status === "CLOSED") return "CLOSED";
    if (ticket.ticket_status === "CANCELED") return "CANCELED";
    if (
      ticket.ticket_status === "PENDING" &&
      ticket.broadcast_status === "matched_in_same_center"
    ) {
      return ticket.is_technician_assigned
        ? "Technician Assigned"
        : "Technician Not Assigned";
    }
    if (ticket.ticket_status === "ACCEPTED") {
      return ticket.is_technician_assigned
        ? "Technician Assigned"
        : "Technician Not Assigned";
    }
    return ticket.ticket_status || "PENDING";
  };

  const getParsedData = (data) => {
    return data?.map((item) => ({
      createdAt: item.createdAt,
      ticket_id: item.ticket_id,
      service_required: item.service_provided_for?.service_name || "-",
      broadcast_status: item.broadcast_status || "-",
      tstatus: getTicketStatus(item),
      customer_name: item.personal_details?.name || "-",
      phone: item.personal_details?.primary_phone?.mobile_number || "-",
      phone_display: item.personal_details?.primary_phone?.mobile_number
        ? `*******${item.personal_details.primary_phone.mobile_number.slice(
            -3
          )}`
        : "-",
      action: item,
      _id: item._id,
      is_technician_assigned: item.is_technician_assigned,
      ticket_status: item.ticket_status,
      time_preference:
        item.time_preference?.time_preference_type ||
        item.time_preference?.specific_date_time ||
        "-",
      ticket_created_by: item.ticket_created_by,
    }));
  };

  // API functions
  const getAllTickets = async () => {
    if (!clientId) return;
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}client/get-all-client-ticket/${clientId}`
      );
      if (res.data.success && res.data.data) {
        const formattedData = getParsedData(res.data.data);
        setTicketsList(formattedData);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      showDialog(true, "error", "Failed to fetch tickets");
    }
    setIsLoading(false);
  };

  // Initialize data
  useEffect(() => {
    getAllTickets();
  }, [clientId]);

  return (
    <Root className={classes.root}>
      {/* Toast Notification */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={toast.open}
        autoHideDuration={toast.duration}
        onClose={closeDialog}
      >
        <Alert
          onClose={closeDialog}
          severity={toast.type}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* Main Data Table */}
      <Box
        sx={{
          // maxWidth: 1200,
          // mx: "auto",
          // overflowX: "auto",
          fixedHeader: true,
          border: "1px solid #e0e0e0",
          borderRadius: 4,
        }}
      >
        <MUIDataTable
          title="Client Requests"
          data={ticketsList}
          columns={columns}
          options={{
            ...options,
            selectableRows: "none",
            responsive: "standard",
            fixedHeader: true,
          }}
        />
      </Box>

      {/* View Ticket Modal */}
      <ViewTicketModal
        open={viewModalOpen}
        handleClose={() => setViewModalOpen(false)}
        ticket={selectedTicket}
      />

      {/* Remark Modal */}
      <RemarkModal
        open={remarkModalOpen}
        handleClose={() => setRemarkModalOpen(false)}
        ticketId={ticketForRemark?._id}
        username={globalState?.clientDetails?.name}
        initialRemarks={ticketForRemark?.remarks?.onboarding_team_remarks}
        processedName={ticketForRemark?.remarks?.onboarding_team_name}
        onRemarkAdded={handleRemarkAdded}
      />

      {/* Cancel Ticket Modal */}
      <Modal
        open={cancelModalOpen}
        onClose={handleCloseCancelModal}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            width: { xs: "90%", sm: 500 },
            maxWidth: "95vw",
          }}
        >
          <Typography
            variant="h6"
            sx={{ textAlign: "center", fontWeight: 600 }}
            style={{ marginBottom: "10px" }}
          >
            Cancel Service Request
          </Typography>

          <Typography
            variant="body2"
            sx={{ textAlign: "center", color: "text.secondary" }}
            style={{ marginBottom: "10px" }}
          >
            Please let us know why you are cancelling this request.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Type your reason here..."
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              onClick={handleCloseCancelModal}
              variant="outlined"
              color="secondary"
              disabled={isLoading}
              style={{ marginRight: "10px" }}
            >
              Go Back
            </Button>
            <Button
              onClick={cancelTicket}
              variant="contained"
              style={{ backgroundColor: "red" }}
              disabled={!remarks.trim() || isLoading}
            >
              {isLoading ? "Processing..." : "Confirm Cancellation"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Root>
  );
};

export default ClientRequests;
